// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the playback speed bar in the top inline-start corner, where the
 * ⌂ Browse button was (owner, 2026-10-20; Browse stays at the bottom
 * right). Mockup: the "XR playback speed" canvas.
 *
 *   [ ◔ 0.25× 0.5× [1×] 2× 5× ]   ← the speeds AYNI 3D offers
 *   [ ▣ ]  Show anchor
 *   [ ⇪ ]  Share
 *
 * - The step the viewer chose is green. A new dataset opens at 1×.
 * - Away from 1×, a green chip by Play says the speed; pressing it goes
 *   back to 1× (`#speed-badge`).
 * - With a live layer on, the steps above what it can keep up with are
 *   greyed (`rtBaseRateCeiling`); pressing one says why. Chosen before the
 *   layer went on, the speed stays chosen and a note says the limit.
 * - A dataset that does not move greys the whole bar; so does a meeting
 *   where the presenter leads (the bar shows the presenter's speed).
 *
 * Greyed steps are `aria-disabled`, not `disabled`, so a press can say why.
 * The control checks the app every half second, as the spin button does.
 */

import { t } from '../i18n'
import { formatNumber } from '../i18n/format'
import { SPEED_STEPS, aboveCeiling, nearestSpeedStep, sameSpeed } from '../services/playbackSpeed'

/** What the bar shows. */
export interface PlaybackSpeedState {
  /** The speed the viewer chose (1: normal). */
  speed: number
  /** False for a dataset that does not move (a still picture): the bar is greyed. */
  moves: boolean
  /** The fastest the live layers let the dataset play; Infinity: no limit. */
  ceiling: number
  /** True while a meeting's presenter leads playback: the bar shows their speed and takes no press. */
  locked: boolean
}

export interface PlaybackSpeedDeps {
  /** The state now; null when no dataset is on the globe. */
  state(): PlaybackSpeedState | null
  /** Play at this speed. */
  set(rate: number): void
  announce?(message: string): void
}

export interface PlaybackSpeedHandle {
  /** Read the app again now (after a press elsewhere, a new dataset). */
  sync(): void
}

/** How long a note said after a refused press stays, ms. */
const NOTE_MS = 4000

/** A speed as the bar writes it: "0.25×", "0,25×" in Spanish. */
export function speedText(rate: number): string {
  return t('speed.value', { rate: formatNumber(rate, { maximumFractionDigits: 2 }) })
}

let syncTimer: number | null = null

/** Wire `#playback-speed` and `#speed-badge`. Safe to call twice. */
export function initPlaybackSpeedControl(deps: PlaybackSpeedDeps): PlaybackSpeedHandle {
  const root = document.getElementById('playback-speed')
  const bar = root?.querySelector<HTMLElement>('.playback-speed-bar') ?? null
  const note = document.getElementById('playback-speed-note')
  const badge = document.getElementById('speed-badge') as HTMLButtonElement | null
  if (!root || !bar || !note) return { sync: () => {} }

  let steps = [...bar.querySelectorAll<HTMLButtonElement>('.playback-speed-step')]
  if (!steps.length) {
    steps = SPEED_STEPS.map((rate) => {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'playback-speed-step'
      b.dataset.speed = String(rate)
      bar.append(b)
      return b
    })
  }

  let shown = ''
  let flashUntil = 0
  let flashText = ''

  const sync = (): void => {
    const s = deps.state()
    const now = Date.now()
    const flashing = now < flashUntil
    const key = s
      ? `${s.speed}|${s.moves}|${s.ceiling}|${s.locked}|${flashing ? flashText : ''}`
      : 'none'
    if (key === shown) return
    shown = key
    if (!s) {
      note.classList.add('hidden')
      badge?.classList.add('hidden')
      return
    }
    // A follower's rate is trimmed to keep in step: it shows as its step.
    const pressed = s.locked ? nearestSpeedStep(s.speed) : s.speed
    const idle = !s.moves
    bar.classList.toggle('is-idle', idle)
    bar.classList.toggle('is-locked', s.locked && !idle)
    bar.title = idle ? t('speed.note.still') : s.locked ? t('speed.note.locked') : ''
    for (const b of steps) {
      const rate = Number(b.dataset.speed)
      const on = !idle && sameSpeed(rate, pressed)
      const capped = !idle && !s.locked && aboveCeiling(rate, s.ceiling)
      b.textContent = speedText(rate)
      b.classList.toggle('active', on)
      b.classList.toggle('is-capped', capped)
      b.setAttribute('aria-pressed', String(on))
      if (idle || s.locked || capped) b.setAttribute('aria-disabled', 'true')
      else b.removeAttribute('aria-disabled')
      const label = rate === 1 ? t('speed.step.normal', { rate: speedText(rate) }) : t('speed.step.aria', { rate: speedText(rate) })
      b.setAttribute('aria-label', label)
      b.title = capped ? t('speed.note.ceiling', { rate: speedText(s.ceiling) }) : label
    }
    // The note: a refused press for a moment; else a chosen speed the live layers hold back.
    const held = !idle && !s.locked && aboveCeiling(s.speed, s.ceiling)
    const text = flashing ? flashText : held ? t('speed.note.ceiling', { rate: speedText(s.ceiling) }) : ''
    note.textContent = text
    note.classList.toggle('hidden', !text)
    if (badge) {
      const away = !idle && !sameSpeed(s.speed, 1)
      badge.classList.toggle('hidden', !away)
      if (away) {
        badge.textContent = speedText(s.speed)
        const label = t('speed.badge.aria', { rate: speedText(s.speed) })
        badge.title = label
        badge.setAttribute('aria-label', label)
      }
    }
  }

  const flash = (text: string): void => {
    flashText = text
    flashUntil = Date.now() + NOTE_MS
    deps.announce?.(text)
    sync()
    window.setTimeout(sync, NOTE_MS + 50)
  }

  const choose = (rate: number): void => {
    deps.set(rate)
    deps.announce?.(t('speed.announce', { rate: speedText(rate) }))
    flashUntil = 0
    sync()
  }

  if (root.dataset.wired !== 'true') {
    root.dataset.wired = 'true'
    bar.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('.playback-speed-step')
      if (!b) return
      const s = deps.state()
      if (!s) return
      const rate = Number(b.dataset.speed)
      if (!s.moves) return flash(t('speed.note.still'))
      if (s.locked) return flash(t('speed.note.locked'))
      if (aboveCeiling(rate, s.ceiling)) return flash(t('speed.note.ceiling', { rate: speedText(s.ceiling) }))
      if (!sameSpeed(rate, s.speed)) choose(rate)
    })
    badge?.addEventListener('click', () => {
      const s = deps.state()
      if (s && !s.locked) choose(1)
    })
  }

  if (syncTimer !== null) window.clearInterval(syncTimer)
  syncTimer = window.setInterval(sync, 500)
  sync()
  return {
    sync: () => {
      shown = ''
      sync()
    },
  }
}
